import { Component, Input, OnChanges } from '@angular/core';
import {
  WeatherDataTypeEnum,
  weatherDataTypeDisplays,
  weatherIconDirectory
} from 'src/app/device/shared/enums/weatherData-type.enum';
import { MAX_WEATHER_DATA_DISPLAY_VALUES } from 'src/app/device/shared/functions/weatherData-display-values';
import { MAX_WEATHER_DETAIL_TYPES } from '../../shared/functions/weather-config';
import { AlertService } from '../../../shared/alert/shared/alert.service';
import { AlertTypes } from '../../../shared/alert/shared/alert-types';
import { DataEndChannel } from '../../shared/interfaces/data-end-channel.interface';
import { DataEndChannelService } from '../../shared/services/data-end-channel.service';
import { WeatherConfig } from '../../shared/interfaces/weather-config.interface';
import {
  WeatherConfigSelection,
  WeatherTypeSelection,
  emptyWeatherConfigSelection,
  toWeatherConfig,
  toWeatherConfigSelection
} from '../../shared/functions/weather-config';

/** One row of the selection: a weather data type with label and icon. */
interface WeatherTypeRow {
  type: WeatherDataTypeEnum;
  label: string;
  unit: string;
  iconPath: string;
}

/**
 * Weather configuration of a route station: which data types the station shows
 * in the schema, and which ones appear in the detail view including the history.
 *
 * Read and written per weather channel (`DataEndChannel/{id}/WeatherConfig`);
 * the settings field of the channel stays untouched on the API side.
 */
@Component({
  selector: 'avs-weather-config-settings',
  templateUrl: './weather-config-settings.component.html',
  styleUrls: ['./weather-config-settings.component.css']
})
export class WeatherConfigSettingsComponent implements OnChanges {
  @Input() weatherChannel?: DataEndChannel;

  /** The schema shows only the first values — selecting more would have no effect. */
  public readonly maxSchemaTypes: number = MAX_WEATHER_DATA_DISPLAY_VALUES;
  /** Upper limit of the detail view, so that view and history stay readable. */
  public readonly maxDetailTypes: number = MAX_WEATHER_DETAIL_TYPES;
  public readonly rows: WeatherTypeRow[] = buildRows();
  public selection: WeatherConfigSelection = emptyWeatherConfigSelection();
  /** Only an actually read configuration may be edited and written back. */
  public isConfigLoaded: boolean = false;
  public isAnyValueChanged: boolean = false;
  public information: string = '';

  constructor(
    private dataEndChannelService: DataEndChannelService,
    private alertService: AlertService
  ) { }

  public ngOnChanges(): void {
    this.selection = emptyWeatherConfigSelection();
    this.isConfigLoaded = false;
    this.isAnyValueChanged = false;
    this.information = '';
    if (!this.weatherChannel) { return; }

    this.dataEndChannelService.getWeatherConfig(this.weatherChannel.id).subscribe({
      next: (config: WeatherConfig) => {
        this.selection = toWeatherConfigSelection(config);
        this.information = this.buildInformation();
        this.isConfigLoaded = true;
      },
      // The error message itself is already shown by the HttpService. Editing
      // stays locked, so an unread configuration cannot be overwritten.
      error: () => {
        this.information = 'Die Konfiguration konnte nicht gelesen werden.';
      }
    });
  }

  public isSchemaSelected(type: WeatherDataTypeEnum): boolean {
    return this.selection.schemaTypes.known.includes(type);
  }

  public isDetailSelected(type: WeatherDataTypeEnum): boolean {
    return this.selection.detailTypes.known.includes(type);
  }

  /** Beyond the display limit nothing more can be selected for the schema. */
  public isSchemaDisabled(type: WeatherDataTypeEnum): boolean {
    return !this.isSchemaSelected(type) && this.selection.schemaTypes.known.length >= this.maxSchemaTypes;
  }

  /** Beyond the limit nothing more can be selected for the detail view. */
  public isDetailDisabled(type: WeatherDataTypeEnum): boolean {
    return !this.isDetailSelected(type) && !this.hasFreeDetailSlot();
  }

  public onSchemaChange(type: WeatherDataTypeEnum, selected: boolean): void {
    toggle(this.selection.schemaTypes, type, selected);
    // What the station shows in the schema is usually wanted in the detail view
    // too — as long as there is room left there.
    if (selected && this.hasFreeDetailSlot()) {
      toggle(this.selection.detailTypes, type, true);
    }
    this.isAnyValueChanged = true;
  }

  public onDetailChange(type: WeatherDataTypeEnum, selected: boolean): void {
    toggle(this.selection.detailTypes, type, selected);
    this.isAnyValueChanged = true;
  }

  public onSaveBtnClicked(): void {
    if (!this.weatherChannel || !this.isConfigLoaded) { return; }

    this.dataEndChannelService.updateWeatherConfig(this.weatherChannel.id, toWeatherConfig(this.selection))
      .subscribe(() => {
        this.alertService.alert('Die Änderungen wurden erfolgreich gespeichert', AlertTypes.info);
        this.isAnyValueChanged = false;
        // The write dropped the unknown types — the view follows, so the hint
        // does not stay behind for a configuration that no longer holds them.
        this.selection.schemaTypes.hasUnknownTypes = false;
        this.selection.detailTypes.hasUnknownTypes = false;
        this.information = this.buildInformation();
      });
  }

  private hasFreeDetailSlot(): boolean {
    return this.selection.detailTypes.known.length < this.maxDetailTypes;
  }

  private buildInformation(): string {
    const hasUnknownTypes = this.selection.schemaTypes.hasUnknownTypes
      || this.selection.detailTypes.hasUnknownTypes;

    return hasUnknownTypes ? 'Konfiguration enthält unbekannte Datentypen' : '';
  }
}

/**
 * The rows of the selection, in the order of `WeatherDataTypeEnum`: taken from
 * the enum's names, because its numeric reverse keys would be sorted by DE-Typ
 * instead. This way a new data type shows up here without further work.
 */
function buildRows(): WeatherTypeRow[] {
  return Object.keys(WeatherDataTypeEnum)
    .filter(key => isNaN(Number(key)))
    .map(name => WeatherDataTypeEnum[name as keyof typeof WeatherDataTypeEnum])
    .map(type => {
      const display = weatherDataTypeDisplays[type];
      return {
        type: type,
        label: display.label,
        unit: display.unit,
        iconPath: weatherIconDirectory + display.icon
      };
    });
}

function toggle(types: WeatherTypeSelection, type: WeatherDataTypeEnum, selected: boolean): void {
  if (selected) {
    if (!types.known.includes(type)) { types.known.push(type); }
    return;
  }
  types.known = types.known.filter(knownType => knownType !== type);
}
